import { useWindowResize } from '@toggle/helpers';
import { TransformedXAxisType } from '@toggle/toggle';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useMemo, useRef } from 'react';

import { formatDecimalPlaces } from '~/components/table/utils';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
} from '../apache-config';
import { tooltipFormatter } from '../utils';

const defaultSeriesNames = ['Increased', 'Decreased'];
const MINUS_SIGN = '-';

type WaterfallSeries = {
  name: string;
  data: (number | null)[];
};

export interface WaterfallChartProps extends ApacheChartBase {
  chartType: 'apache-waterfall';
  xAxis: TransformedXAxisType;
  series: number[];
  seriesNames?: string[];
}

export const WaterfallChart = ({
  theme = 'dark_default',
  chartType,
  xAxis,
  series,
  seriesNames = defaultSeriesNames,
  onInit,
  isModal,
}: WaterfallChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  const absoluteSeries = series.map(i => Math.abs(i));

  const formattedSeries = useMemo(() => {
    return absoluteSeries.reduce<WaterfallSeries[]>(
      (result, current, index) => {
        if (index % 2 === 0) {
          result[0].data.push(current);
          result[1].data.push(null);
        } else {
          result[0].data.push(null);
          result[1].data.push(current);
        }

        return result;
      },
      [
        { name: seriesNames[0], data: [] },
        { name: seriesNames[1], data: [] },
      ]
    );
  }, [series, seriesNames]);

  const placeholderData = useMemo(() => {
    if (!absoluteSeries.length) {
      return [];
    }

    if (absoluteSeries.length === 1) {
      return [0];
    }

    return absoluteSeries.slice(2).reduce(
      (acc, currentValue, index) => {
        const actualIndex = index + 2;

        if (actualIndex % 2 === 0) {
          acc.push(acc[acc.length - 1]);
        } else {
          const prevIndex = actualIndex - 1;
          const newValue =
            absoluteSeries[prevIndex] + acc[acc.length - 1] - currentValue;
          acc.push(newValue);
        }

        return acc;
      },
      [0, absoluteSeries[0] - absoluteSeries[1]]
    );
  }, [formattedSeries]);

  useEffect(() => {
    const chartDom = init(ref.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const option = createApacheConfig({
      theme,
      isModal,
      options: {
        animationDuration: 2_000,
        xAxis: {
          type: 'category',
          data: xAxis,
        },
        yAxis: {
          type: 'value',
        },
        legend: {
          show: true,
          data: seriesNames.map(i => i),
        },
        series: [
          {
            name: 'placeholder',
            type: 'bar',
            stack: 'Total',
            silent: true,
            itemStyle: {
              borderColor: 'transparent',
              color: 'transparent',
            },
            emphasis: {
              itemStyle: {
                borderColor: 'transparent',
                color: 'transparent',
              },
            },
            data: placeholderData,
          },
          {
            ...formattedSeries[0],
            type: 'bar',
            stack: 'Total',
            label: {
              show: true,
              position: 'top',
              formatter: (
                params: echarts.DefaultLabelFormatterCallbackParams
              ) => {
                const value = params.value || 0;
                return formatDecimalPlaces(Number(value)).toString();
              },
            },
          },
          {
            ...formattedSeries[1],
            type: 'bar',
            stack: 'Total',
            label: {
              show: true,
              position: 'bottom',
              formatter: (
                params: echarts.DefaultLabelFormatterCallbackParams
              ) => {
                const value = params.value || 0;
                return `${MINUS_SIGN}${formatDecimalPlaces(
                  Number(value)
                ).toString()}`;
              },
            },
          },
        ],
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (
            params:
              | echarts.TooltipComponentFormatterCallbackParams
              | echarts.TooltipComponentFormatterCallbackParams[]
          ) => {
            let tar;

            if (Array.isArray(params)) {
              const increasedValue =
                (params[1] as echarts.DefaultLabelFormatterCallbackParams)
                  ?.value || undefined;

              if (increasedValue !== undefined && increasedValue !== null) {
                tar = params[1];
              } else {
                tar = params[2];
              }
            } else {
              tar = params;
            }

            return tooltipFormatter({
              isShowTitle: true,
              isShowMarker: true,
              params: tar as Types.DefaultLabelFormatterCallbackParams,
            });
          },
        },
      },
    });

    chartDom.setOption(option);

    if (ref.current) {
      onInit?.(ref.current);
    }

    return () => {
      chartDom.dispose();
    };
  }, [chartType, theme, xAxis, series, seriesNames, width, height, isModal]);

  return <div ref={ref} />;
};
